import type { Timezone } from '@/datetime/timezone.service';
import type { StatisticsSummaryItem } from '@/summaryReport/summaryData.types';
import { SummaryReportItemView } from '@/summaryReport/SummaryReportItemView.molecule';
import { SummaryReportWindowOfInterest } from '@/summaryReport/SummaryReportWindowOfInterest.molecule';
import { SummaryReportAlert } from '@/summaryReport/SummaryReportAlert.molecule';
import { hasNoData, hasError } from '@/summaryReport/summaryReport.utilities';
import clsx from 'clsx';
import React from 'react';
import tinycolor from 'tinycolor2';
import { SummaryReportTitleHeader } from './SummaryReportTitleHeader.molecule';

export interface SummaryReportTitleCardProps {
  item: StatisticsSummaryItem;
  isPresentation?: boolean;
  isOverlaid?: boolean;
  signal?: any;
  condition?: any;
  timezone?: Timezone;
  showSignal?: boolean;
  showTimeRange?: boolean;
  showCondition?: boolean;
  onEdit: (id: string) => void;
  onDuplicate: (id: string) => void;
  onClose: (item: StatisticsSummaryItem) => void;
}

export const SummaryReportTitleCard = React.forwardRef<HTMLDivElement, SummaryReportTitleCardProps>(
  (
    {
      item,
      isPresentation,
      isOverlaid,
      signal,
      condition,
      timezone,
      showSignal,
      showTimeRange,
      showCondition,
      onEdit,
      onDuplicate,
      onClose,
    },
    ref,
  ) => {
    const noData = hasNoData(item);
    const itemHasError = hasError(item);
    const intervalStart = item.data?.inputs?.intervalStart;
    const intervalEnd = item.data?.inputs?.intervalEnd;
    const isTitleOnlyPresentation = isPresentation && !showSignal && !showTimeRange && !showCondition;
    const backgroundColor =
      isTitleOnlyPresentation || !item?.color ? 'transparent' : tinycolor(item.color).setAlpha(0.1).toRgbString();

    return (
      <div
        ref={ref}
        className={clsx('summaryReportTitleCard flexRowContainer alert alert-info pr10 pl10 pt5 pb5 width-330', {
          forceNoBorder: isTitleOnlyPresentation,
          flexFill: !isOverlaid,
        })}
        style={{ backgroundColor }}>
        <SummaryReportTitleHeader
          item={item}
          isPresentation={isPresentation}
          onEdit={() => onEdit(item.id)}
          onDuplicate={() => onDuplicate(item.id)}
          onClose={() => onClose(item)}
        />
        {showTimeRange && timezone && (
          <SummaryReportWindowOfInterest
            intervalStart={intervalStart}
            intervalEnd={intervalEnd}
            timezone={timezone}
            isPresentation={isPresentation}
          />
        )}
        {showSignal && signal && (
          <SummaryReportItemView
            item={signal}
            isPresentation={isPresentation}
            showAncestors={true}
            showUnitOfMeasure={true}
            dataTestId="specSelectedSignal"
          />
        )}
        {showCondition && condition && (
          <SummaryReportItemView
            item={condition}
            isPresentation={isPresentation}
            showAncestors={true}
            dataTestId="specSelectedCondition"
          />
        )}
        {(noData || itemHasError) && <SummaryReportAlert errorMessage={item.error?.message} />}
      </div>
    );
  },
);
